import { camelCase } from 'lodash';

export enum GroupTypeApiName {
  PROVIDER = 'provider',
  MANAGER = 'manager',
}

type Type = {
  apiName: GroupTypeApiName;
  displayName: string;
};

export type ManagerGroupTypeConfig = {
  canReceiveEscalations: boolean;
  patientPhysicianTeamRequired: boolean;
  patientTransferredFromRequired: boolean;
};

export type ProviderGroupTypeConfig = {
  designatesAcceptedPlans: boolean;
  hideDischargeSummaryPrompt: boolean;
  hideDischargedAma: boolean;
};

export interface GroupTypeOptions {
  id: string;
  apiName: string;
  displayName: string;
  name: string;
  isInpatient: boolean;
  config: ManagerGroupTypeConfig | ProviderGroupTypeConfig | Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
  type: Type;
  translations?: Record<string, string>;
}

function getDefaults(): GroupTypeOptions {
  return {
    id: '',
    apiName: '',
    displayName: '',
    name: '',
    isInpatient: false,
    config: {},
    createdAt: null,
    updatedAt: null,
    type: {
      apiName: GroupTypeApiName.PROVIDER,
      displayName: '',
    },
    translations: {},
  };
}
/**
 * @class GroupType
 * @classdesc Represents a group type in the system
 * @property {string} id - The group type's id
 * @property {string} apiName - The group type's api name
 * @property {string} displayName - The group type's display name
 * @property {string} name - The group type's name
 * @property {boolean} isInpatient - Whether the group type is inpatient
 * @property {Record<string, unknown>} config - The group type's config
 * @property {string | null} createdAt - The group type's creation date
 * @property {string | null} updatedAt - The group type's update date
 * @property {Type} type - The group type's type
 * @param {Partial<GroupTypeOptions>} [options={}]
 * @example const groupType = new GroupType({ id: '123' });
 */
export default class GroupType {
  id: string;
  apiName: string;
  displayName: string;
  name: string;
  isInpatient: boolean;
  config: Record<string, unknown>;
  createdAt: string | null;
  updatedAt: string | null;
  type: Type;
  translations: Record<string, string>;

  constructor(options: Partial<GroupTypeOptions> = {}) {
    const opts = { ...getDefaults(), ...options };
    this.id = opts.id;
    this.apiName = opts.apiName;
    this.displayName = opts.displayName;
    this.name = opts.name;
    this.isInpatient = opts.isInpatient;
    this.config = opts.config;
    this.createdAt = opts.createdAt;
    this.updatedAt = opts.updatedAt;
    this.type = opts.type as Type;
    this.translations = opts.translations ?? {};
  }

  get isProvider() {
    return this.type.apiName === GroupTypeApiName.PROVIDER;
  }

  get isManager() {
    return this.type.apiName === GroupTypeApiName.MANAGER;
  }

  get camelCaseApiName() {
    return camelCase(this.apiName);
  }
}
